/**
 * Redirect State Management for Iframe Flow
 *
 * When users complete payments via GoCardless, they're redirected to their bank
 * which breaks out of the iframe. This module saves/restores context so we can
 * handle the return properly.
 */

const STORAGE_KEY = 'giftaid_redirect_state';
const STATE_TTL_MS = 30 * 60 * 1000; // 30 minutes

export interface RedirectState {
  donationId: string;
  charityId: string;
  isEmbed: boolean;
  customReturnUrl?: string;
  parentOrigin?: string;
  timestamp: number;
}

/**
 * Save redirect state before GoCardless redirects to bank
 */
export function saveRedirectState(state: Omit<RedirectState, 'timestamp'>): void {
  if (typeof window === 'undefined') return;

  try {
    const stateWithTimestamp: RedirectState = {
      ...state,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithTimestamp));
  } catch (error) {
    // sessionStorage might be disabled
    console.warn('Failed to save redirect state:', error);
  }
}

/**
 * Get redirect state after returning from bank
 * Returns null if no state, expired, or sessionStorage unavailable
 */
export function getRedirectState(): RedirectState | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const state: RedirectState = JSON.parse(stored);

    // Check if state has expired
    if (Date.now() - state.timestamp > STATE_TTL_MS) {
      clearRedirectState();
      return null;
    }

    return state;
  } catch (error) {
    console.warn('Failed to get redirect state:', error);
    return null;
  }
}

/**
 * Clear redirect state after handling return
 */
export function clearRedirectState(): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear redirect state:', error);
  }
}

/**
 * Check if we're returning from a redirect (has valid state)
 */
export function hasRedirectState(): boolean {
  return getRedirectState() !== null;
}
